import { Link } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { ABOUT_CONTENT } from "../lib/aboutContent";
import { Wrench, ArrowRight, Heart, Globe, Lock, MapPin, Users } from "lucide-react";

const ICONS = [Heart, MapPin, Users, Globe, Lock];
const SUPPORT_EMAIL = "support@stopchlamas.lt";

export default function About() {
  const { lang, t } = useI18n();
  const c = ABOUT_CONTENT[lang] || ABOUT_CONTENT.lt;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="about-page">
      {/* Hero */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#C85A32] rounded-md flex items-center justify-center">
          <Wrench className="w-6 h-6 text-white" />
        </div>
        <span className="font-heading font-black text-xl tracking-tight text-[#1A1A18]">
          StopChlamas
        </span>
      </div>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-black tracking-tight text-[#1A1A18]">
        {c.title}
      </h1>

      <div className="mt-6 space-y-4 text-base leading-relaxed text-[#1A1A18] max-w-3xl">
        <p>{c.intro}</p>
        <p>{c.intro2}</p>
      </div>

      {/* Principles */}
      <section className="mt-14">
        <h2 className="text-lg font-heading font-bold uppercase tracking-widest text-[#7A7A73]">
          {c.principles_title}
        </h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {c.principles.map((pr, idx) => {
            const Icon = ICONS[idx % ICONS.length];
            return (
              <div
                key={idx}
                data-testid={`about-principle-${idx}`}
                className="p-5 border border-[#D1CFC7] rounded-md bg-white hover:border-[#C85A32] transition-colors"
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex-shrink-0 bg-[#F0EFEB] rounded-md flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#C85A32]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-[#1A1A18]">{pr.h}</h3>
                    <p className="mt-1 text-sm text-[#4A4A45] leading-relaxed">{pr.p}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Why */}
      <section className="mt-14 bg-[#1A1A18] text-white rounded-md p-8 md:p-12">
        <h2 className="text-3xl font-heading font-black tracking-tight">{c.why_title}</h2>
        <p className="mt-4 text-base text-white/80 leading-relaxed max-w-3xl">{c.why_p}</p>
      </section>

      {/* CTA */}
      <section className="mt-14 max-w-3xl">
        <h2 className="text-2xl font-heading font-black text-[#1A1A18]">{c.join_title}</h2>
        <p className="mt-3 text-base text-[#4A4A45] leading-relaxed">{c.join_p}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to="/post"
            data-testid="about-cta-post"
            className="btn-primary inline-flex items-center gap-2"
          >
            {t("nav_post")} <ArrowRight className="w-4 h-4" />
          </Link>
          <Link
            to="/browse"
            data-testid="about-cta-browse"
            className="btn-secondary inline-flex items-center gap-2"
          >
            {t("cta_browse")}
          </Link>
        </div>
        <p className="mt-8 text-sm text-[#7A7A73]">
          {c.contact_p}{" "}
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            data-testid="about-email-link"
            className="font-bold text-[#C85A32] hover:underline"
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
      </section>
    </div>
  );
}
